import { getSseUrl, isDebugMode } from '@/config/env';

// Toggle SSE logging here. Set to `true` to re-enable logs for debugging.
const SSE_DEBUG = false;
const sseLog = (...args: any[]) => {
  if (SSE_DEBUG) console.log(...args);
};
const sseWarn = (...args: any[]) => {
  if (SSE_DEBUG) console.warn(...args);
};
const sseError = (...args: any[]) => {
  if (SSE_DEBUG) console.error(...args);
};

export interface SSEEventData {
  id?: string;
  type: string;
  data: any;
  retry?: number;
}

export interface SSEServiceConfig {
  storeId: string;
  onEvent: (event: SSEEventData) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export class SSEService {
  private eventSource: EventSource | null = null;
  private config: SSEServiceConfig;
  private reconnectAttempts = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private shouldReconnect = true;

  constructor(config: SSEServiceConfig) {
    this.config = {
      reconnectInterval: 3000, // 3 seconds
      maxReconnectAttempts: 10,
      ...config,
    };
  }

  // If EventSource fails to establish a connection, use fetch with credentials as a fallback
  private async maybeStartFetchFallback(url: string) {
    // Wait briefly for EventSource to open
    await new Promise((res) => setTimeout(res, 2500));

    if (this.eventSource && this.eventSource.readyState === EventSource.OPEN) return;

    sseWarn("📡 SSE EventSource not open, attempting fetch-based SSE fallback. This will include cookies.");

    try {
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok || !resp.body) {
        sseError("❌ Fetch SSE fallback failed to connect", resp.status, resp.statusText);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      const readLoop = async () => {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let index: number;
          while ((index = buffer.indexOf("\n\n")) !== -1) {
            const chunk = buffer.slice(0, index).trim();
            buffer = buffer.slice(index + 2);
            this.processSSEChunk(chunk);
          }
        }
      };

      readLoop().catch((err) => sseError("❌ SSE fetch read loop error:", err));
    } catch (error) {
      sseError("❌ Fetch-based SSE fallback failed:", error);
    }
  }

  private processSSEChunk(chunk: string) {
    // Parse simple SSE chunk into event/data pairs
    const lines = chunk.split(/\r?\n/);
    let eventType = "message";
    let dataLines: string[] = [];

    for (const line of lines) {
      if (line.startsWith("event:")) {
        eventType = line.replace(/^event:\s*/, "");
      } else if (line.startsWith("data:")) {
        dataLines.push(line.replace(/^data:\s*/, ""));
      }
    }

    const dataStr = dataLines.join("\n");
    try {
      const parsed = JSON.parse(dataStr);
      this.config.onEvent({ type: eventType, data: parsed });
    } catch (err) {
      this.config.onEvent({ type: eventType, data: dataStr });
    }
  }

  connect(): void {
    if (this.eventSource?.readyState === EventSource.OPEN) {
      return;
    }

    this.shouldReconnect = true;
    this.createConnection();
  }

  private createConnection(): void {
    try {
      // Construct the SSE URL - match API client behavior
      // Allow fallback to parse storeId from the current URL if not provided
      let storeId = this.config.storeId;
      if (!storeId && typeof window !== "undefined") {
        const parts = window.location.pathname.split("/").filter(Boolean);
        // expected path: /:storeType/:storeId/...
        if (parts.length >= 2) storeId = parts[1];
      }

      // Use centralized env configuration
      const baseUrl = getSseUrl();
      const url = `${baseUrl}/api/v1/admin/${storeId}/sse`;

      if (isDebugMode()) {
        sseLog("📡 SSE Configuration:", {
          baseUrl,
          fullUrl: url,
        });
      }
      sseLog("📡 Connecting to SSE:", url);

      // Use EventSource with credentials when available
      // Note: some browsers require the server to allow credentials via CORS
      this.eventSource = new EventSource(url, { withCredentials: true } as any);

      // Connection opened
      this.eventSource.onopen = (event) => {
        sseLog("✅ SSE Connection established", event);
        this.reconnectAttempts = 0;
        this.config.onConnect?.();
      };

      // Handle errors - only handle actual errors, not temporary connection states
      this.eventSource.onerror = (event) => {
        sseError("❌ SSE Connection error:", event);
        sseLog("ReadyState:", this.eventSource?.readyState);

        // Only handle as error if connection is actually closed
        if (this.eventSource?.readyState === EventSource.CLOSED) {
          this.config.onError?.(event);

          if (
            this.shouldReconnect &&
            this.reconnectAttempts < (this.config.maxReconnectAttempts || 10)
          ) {
            this.scheduleReconnect();
          }
        }
      };

      // Handle generic messages - parse JSON when possible
      this.eventSource.onmessage = (event) => {
        try {
          const eventData: SSEEventData = {
            id: event.lastEventId,
            type: event.type || "message",
            data: JSON.parse(event.data),
          };

          this.config.onEvent(eventData);
        } catch (error) {
          const eventData: SSEEventData = {
            id: event.lastEventId,
            type: event.type || "message",
            data: event.data,
          };
          this.config.onEvent(eventData);
        }
      };

      // Listen for specific event types
      this.setupEventListeners();

      // If EventSource doesn't connect within a short timeout, fall back to fetch-based SSE
      this.maybeStartFetchFallback(url);
    } catch (error) {
      sseError("❌ Failed to create SSE connection:", error);
      this.config.onError?.(error as Event);
    }
  }

  private setupEventListeners(): void {
    if (!this.eventSource) return;

    // Product events
    this.eventSource.addEventListener(
      "product.created",
      this.handleEvent.bind(this),
    );
    this.eventSource.addEventListener(
      "product.updated",
      this.handleEvent.bind(this),
    );
    this.eventSource.addEventListener(
      "product.deleted",
      this.handleEvent.bind(this),
    );

    // Category events
    this.eventSource.addEventListener(
      "category.created",
      this.handleEvent.bind(this),
    );
    this.eventSource.addEventListener(
      "category.updated",
      this.handleEvent.bind(this),
    );
    this.eventSource.addEventListener(
      "category.deleted",
      this.handleEvent.bind(this),
    );

    // Order events
    this.eventSource.addEventListener(
      "order.created",
      this.handleEvent.bind(this),
    );
    this.eventSource.addEventListener(
      "order.updated",
      this.handleEvent.bind(this),
    );
    this.eventSource.addEventListener(
      "order.status_changed",
      this.handleEvent.bind(this),
    );

    // Inventory events
    this.eventSource.addEventListener(
      "inventory.updated",
      this.handleEvent.bind(this),
    );
    this.eventSource.addEventListener(
      "inventory.low_stock",
      this.handleEvent.bind(this),
    );

    // Notification events
    this.eventSource.addEventListener(
      "notification",
      this.handleEvent.bind(this),
    );
    this.eventSource.addEventListener("alert", this.handleEvent.bind(this));

    // System events
    this.eventSource.addEventListener(
      "system.maintenance",
      this.handleEvent.bind(this),
    );
    this.eventSource.addEventListener(
      "system.update",
      this.handleEvent.bind(this),
    );

    // Generic events
    this.eventSource.addEventListener("ping", this.handleEvent.bind(this));
    this.eventSource.addEventListener("heartbeat", this.handleEvent.bind(this));
  }

  private handleEvent(event: MessageEvent): void {
    try {
      const eventData: SSEEventData = {
        id: event.lastEventId,
        type: event.type,
        data: JSON.parse(event.data),
      };

        sseLog(`📡 SSE Event [${event.type}]:`, eventData);
      this.config.onEvent(eventData);
      } catch (error) {
      sseError(
        `❌ Failed to parse SSE event [${event.type}]:`,
        error,
        event.data,
      );

      // Still trigger the event with raw data
      const eventData: SSEEventData = {
        id: event.lastEventId,
        type: event.type,
        data: event.data,
      };
      this.config.onEvent(eventData);
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.config.reconnectInterval! * Math.pow(2, this.reconnectAttempts - 1),
      30000, // Max 30 seconds
    );

    sseLog(
      `🔄 Scheduling SSE reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`,
    );

    this.reconnectTimer = setTimeout(() => {
      if (this.shouldReconnect) {
        this.createConnection();
      }
    }, delay);
  }

  disconnect(): void {
    this.shouldReconnect = false;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.config.onDisconnect?.();
    }

    this.reconnectAttempts = 0;
  }

  getConnectionState(): number {
    return this.eventSource?.readyState ?? EventSource.CLOSED;
  }

  isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN;
  }

  isConnecting(): boolean {
    return this.eventSource?.readyState === EventSource.CONNECTING;
  }
}
